import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../core/data/redux/store";
import {
    setCurrentUserLocation,
    setLocationError,
    clearLocationError,
    setLocationVerificationStatus,
    setAllowedRadius
} from "../../core/data/redux/locationSlice";
import { calculateDistanceInMeters } from "../../utils/geoUtils";


interface GeoLocationModalProps {
    show: boolean;
    onClose: () => void;
    onLocationVerified?: (isWithinRadius: boolean, distance: number) => void;
    allowedRadius?: number; // in meters
}

interface LocationData {
    latitude: number;
    longitude: number;
    accuracy?: number;
}

const GeoLocationModal: React.FC<GeoLocationModalProps> = ({
    show,
    onClose,
    onLocationVerified,
    allowedRadius = 150 // Default 150 meters
}) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [distance, setDistance] = useState<number | null>(null);
    const [hasCheckedLocation, setHasCheckedLocation] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [failureMessage, setFailureMessage] = useState<string>('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [showFailure, setShowFailure] = useState(false);

    // Get data from Redux store
    const user = useSelector((state: RootState) => state.user.Users);
    const selectedSchool = useSelector((state: RootState) => state.school.selectedSchool);
    const currentUserLocation = useSelector((state: RootState) => state.userLocation.currentUserLocation);
    const locationError = useSelector((state: RootState) => state.userLocation.error);
    const isLocationVerified = useSelector((state: RootState) => state.userLocation.isLocationVerified);
    const storedAllowedRadius = useSelector((state: RootState) => state.userLocation.allowedRadius);

    const currentUser = user.length > 0 ? user[0] : null;
    const schoolName = currentUser?.relatedEntity?.schools?.name || 'School Name Not Available';

    // Get school coordinates from selected school or user data
    const schoolCoordinates = selectedSchool || currentUser?.relatedEntity?.schools || null;

    // Set allowed radius in Redux when component mounts
    useEffect(() => {
        if (allowedRadius !== storedAllowedRadius) {
            dispatch(setAllowedRadius(allowedRadius));
        }
    }, [allowedRadius, storedAllowedRadius, dispatch]);

    // Check if geolocation is supported
    const isGeolocationSupported = () => {
        return 'geolocation' in navigator;
    };

    // Get current location
    const getCurrentLocation = useCallback(() => {
        if (!isGeolocationSupported()) {
            dispatch(setLocationError('Geolocation is not supported by your browser.'));
            return;
        }

        setIsLoading(true);
        dispatch(clearLocationError());
        setShowSuccess(false);
        setShowFailure(false);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const location: LocationData = {
                    latitude: position.coords.latitude || 0,
                    longitude: position.coords.longitude || 0,
                    accuracy: position.coords.accuracy || 0
                };

                // Store location data in Redux store
                dispatch(setCurrentUserLocation({
                    latitude: location.latitude,
                    longitude: location.longitude,
                    accuracy: location.accuracy,
                    timestamp: new Date().toISOString()
                }));

                setIsLoading(false);
                setHasCheckedLocation(true);

                // Calculate distance if school coordinates are available
                if (schoolCoordinates?.latitude && schoolCoordinates?.longitude &&
                    typeof schoolCoordinates.latitude === 'number' &&
                    typeof schoolCoordinates.longitude === 'number') {
                    const calculatedDistance = calculateDistanceInMeters(
                        location.latitude,
                        location.longitude,
                        schoolCoordinates.latitude,
                        schoolCoordinates.longitude
                    );

                    setDistance(calculatedDistance);
                    const withinRadius = calculatedDistance <= allowedRadius;

                    // Store verification status in Redux
                    dispatch(setLocationVerificationStatus({
                        isVerified: withinRadius,
                        distance: calculatedDistance,
                        allowedRadius: allowedRadius
                    }));

                    // Set status message
                    const statusMessage = withinRadius
                        ? 'You are within the allowed radius of the school. Attendance marking is allowed.'
                        : 'You are outside the allowed radius. You are not allowed to mark attendance.';

                    if (withinRadius) {
                        setSuccessMessage(statusMessage);
                        setShowSuccess(true);
                    } else {
                        setFailureMessage(statusMessage);
                        setShowFailure(true);
                    }

                    // Call callback if provided
                    if (onLocationVerified) {
                        onLocationVerified(withinRadius, calculatedDistance);
                    }
                } else {
                    dispatch(setLocationError('School coordinates not available or invalid.'));
                }
            },
            (error) => {
                setIsLoading(false);
                let errorMessage = 'Failed to get your location.';

                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location permission denied. Please enable location access.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out.';
                        break;
                }

                dispatch(setLocationError(errorMessage));
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 60000
            }
        );
    }, [dispatch, schoolCoordinates, allowedRadius, onLocationVerified]);

    // Auto-trigger location check when modal opens
    useEffect(() => {
        if (show && !hasCheckedLocation) {
            getCurrentLocation();
        }
    }, [show, hasCheckedLocation, getCurrentLocation]);

    // Reset state when modal closes
    const handleClose = () => {
        setDistance(null);
        setHasCheckedLocation(false);
        setSuccessMessage('');
        setFailureMessage('');
        setShowSuccess(false);
        setShowFailure(false);
        dispatch(clearLocationError());
        onClose();
    };

    const getDistanceText = () => {
        if (distance === null || distance === undefined) return 'N/A';
        return distance < 1000
            ? `${Math.round(distance)} meters`
            : `${(distance / 1000).toFixed(2)} km`;
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="ti ti-map-pin me-2"></i>
                    Location Verification
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="text-center mb-4">
                    <h5>Welcome to {schoolName || 'Your School'}</h5>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status" className="mb-3">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p>Getting your current location...</p>
                    </div>
                )}

                {/* Error State */}
                {locationError && (
                    <Alert variant="danger" className="mb-3">
                        <i className="ti ti-alert-circle me-2"></i>
                        {locationError}
                    </Alert>
                )}

                {/* Success Status */}
                {showSuccess && (
                    <Alert variant="success" className="mb-3">
                        <div className="d-flex align-items-center">
                            <i className="ti ti-check-circle me-2"></i>
                            <div>
                                <strong>Success:</strong> {successMessage}
                            </div>
                        </div>
                    </Alert>
                )}

                {/* Failure Status */}
                {showFailure && (
                    <Alert variant="danger" className="mb-3">
                        <div className="d-flex align-items-center">
                            <i className="ti ti-alert-circle me-2"></i>
                            <div>
                                <strong>Warning:</strong> {failureMessage}
                            </div>
                        </div>
                    </Alert>
                )}

                {/* Your Coordinates and School Coordinates */}
                {currentUserLocation && schoolCoordinates && (
                    <div className="mt-4">
                        <div className="mb-2">
                            <strong>📍 Your Coordinates:</strong><br />
                            Lat: {currentUserLocation?.latitude?.toFixed(6)} | Lng: {currentUserLocation?.longitude?.toFixed(6)}
                        </div>
                        <div className="mb-2">
                            <strong>🏫 School Coordinates:</strong><br />
                            Lat: {schoolCoordinates?.latitude?.toFixed(6)} | Lng: {schoolCoordinates?.longitude?.toFixed(6)}
                        </div>
                        <div className="mb-2">
                            <strong>📏 Distance:</strong> {getDistanceText()}
                        </div>
                        {currentUserLocation?.accuracy && (
                            <div className="mb-2">
                                <strong>🎯 Accuracy:</strong> ±{Math.round(currentUserLocation?.accuracy || 0)}m
                            </div>
                        )}
                        <div className="mb-2">
                            <strong>✅ Verification Status:</strong> {isLocationVerified === true ? 'Verified' : isLocationVerified === false ? 'Not Verified' : 'Not Checked'}
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    <i className="ti ti-x me-2"></i>
                    Close
                </Button>
                <Button
                    variant="primary"
                    onClick={getCurrentLocation}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Checking...
                        </>
                    ) : (
                        <>
                            <i className="ti ti-refresh me-2"></i>
                            Check Again
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default GeoLocationModal;